import { BRIDGE_ERROR, BRIDGE_METHODS } from '../../constants/bridge';
import { fail, ok } from '../result';

const BIOMETRIC_UNAVAILABLE = 'El navegador no ofrece desbloqueo biométrico local a esta app.';
const HAPTICS_UNAVAILABLE = 'El navegador no permite comprobar si la vibración ocurrió.';

function permissionState(NotificationApi) {
    if (typeof NotificationApi !== 'function') return 'unsupported';
    const raw = NotificationApi.permission;
    if (raw === 'granted' || raw === 'denied') return raw;
    return 'prompt';
}

// Plain browser host. It only reports what the browser can really do:
// haptics and biometrics are always UNAVAILABLE because the web platform gives
// no verifiable equivalent, and nothing here requests a permission on load.
export function createWebAdapter({ win = window } = {}) {
    const nav = win.navigator;
    const NotificationApi = win.Notification;
    const hasShare = typeof nav?.share === 'function';
    const hasNotifications = typeof NotificationApi === 'function';

    const methods = {
        [BRIDGE_METHODS.CAPABILITIES]: async () => ok({
            platform: 'web',
            host: 'browser',
            share: hasShare,
            haptics: false,
            localNotifications: hasNotifications,
            biometric: { available: false, kind: 'none', reason: BIOMETRIC_UNAVAILABLE },
        }),

        [BRIDGE_METHODS.BIOMETRIC_AUTHENTICATE]: async () => fail(BRIDGE_ERROR.UNAVAILABLE, BIOMETRIC_UNAVAILABLE),

        [BRIDGE_METHODS.NOTIFICATION_PERMISSION]: async () => ok({ state: permissionState(NotificationApi) }),

        [BRIDGE_METHODS.NOTIFICATION_REQUEST]: async () => {
            if (!hasNotifications) return fail(BRIDGE_ERROR.UNAVAILABLE);
            try {
                await NotificationApi.requestPermission();
                return ok({ state: permissionState(NotificationApi) });
            } catch {
                return fail(BRIDGE_ERROR.FAILED);
            }
        },

        [BRIDGE_METHODS.NOTIFICATION_LOCAL]: async ({ title, body }) => {
            if (!hasNotifications) return fail(BRIDGE_ERROR.UNAVAILABLE);
            if (permissionState(NotificationApi) !== 'granted') return fail(BRIDGE_ERROR.PERMISSION_DENIED);
            try {
                new NotificationApi(title, { body, tag: 'mastermind-local-test' });
                return ok({ scheduled: true });
            } catch {
                // Android Chrome only shows notifications through a service
                // worker registration, which this draft deliberately omits.
                return fail(BRIDGE_ERROR.UNAVAILABLE, 'Este navegador solo muestra notificaciones desde un service worker.');
            }
        },

        [BRIDGE_METHODS.SHARE]: async (payload) => {
            if (!hasShare) return fail(BRIDGE_ERROR.UNAVAILABLE);
            if (typeof nav.canShare === 'function' && !nav.canShare(payload)) {
                return fail(BRIDGE_ERROR.INVALID_ARGUMENT);
            }
            try {
                await nav.share(payload);
                return ok({ shared: true });
            } catch (error) {
                if (error?.name === 'AbortError') return fail(BRIDGE_ERROR.CANCELLED);
                if (error?.name === 'NotAllowedError') return fail(BRIDGE_ERROR.PERMISSION_DENIED);
                return fail(BRIDGE_ERROR.FAILED);
            }
        },

        [BRIDGE_METHODS.HAPTIC]: async () => fail(BRIDGE_ERROR.UNAVAILABLE, HAPTICS_UNAVAILABLE),
    };

    return { host: 'browser', methods };
}
